'use client';

import { FC, ReactNode } from 'react';

import { AuthFormSubmit } from '@/authentication/components/AuthFormSubmit/AuthFormSubmit';
import { SSOFacebook } from '@/authentication/components/SSO/SSOFacebook';
import { SSOGoogle } from '@/authentication/components/SSO/SSOGoogle';
import { onSubmitSuccessTracking } from '@/authentication/helpers/authentication.helpers';
import { AuthFormContainer } from '@/authentication/layout/AuthFormContainer';
import { AuthFormFooter } from '@/authentication/sections/AuthFormFooter/AuthFormFooter';
import { AuthFormHeader } from '@/authentication/sections/AuthFormHeader/AuthFormHeader';
import { BADGE } from '@/general/components/BadgeIllustration/BadgeIllustration.type';
import { Divider } from '@/general/components/Divider/Divider';
import { InputEmailForm } from '@/general/components/forms/Input/InputEmailForm';
import { StepForm } from '@/steps-form/components/StepForm/StepForm';

import { emailVerificationAction } from './Start.action';
import { StartSchemaFields } from './Start.schema';

type Props = {
  authFormData: Partial<StartSchemaFields>;
  title: ReactNode;
  subTitle: ReactNode;
  ssoGoogleLabel: ReactNode;
  ssoFacebookLabel: ReactNode;
  signupTerms: ReactNode;
  footerCopies: {
    icon: BADGE;
    copy: ReactNode;
  }[];
};

export const Start: FC<Props> = ({
  authFormData,
  title,
  subTitle,
  ssoGoogleLabel,
  ssoFacebookLabel,
  signupTerms,
  footerCopies,
}) => {
  const isCheckout = authFormData.display_state === 'checkout';
  const isWatchlist = authFormData.display_state === 'watchlist';

  return (
    <AuthFormContainer id="auth-start">
      <AuthFormHeader title={title} subtitle={subTitle} />
      <StepForm
        action={emailVerificationAction}
        onSuccess={() => onSubmitSuccessTracking('start')}
        withRecaptcha="auth"
        renderContent={(state) => (
          <>
            <InputEmailForm
              id="email"
              name="email"
              label="Email address"
              defaultValue={authFormData.email}
              formState={state}
            />
            <input type="hidden" name="origin" value={authFormData.origin} />
            <input type="hidden" name="redirect_url" value={authFormData.redirect_url} />
            <input type="hidden" name="display_state" value={authFormData.display_state} />
            {isWatchlist && (
              <input type="hidden" name="watchlist_data" value={JSON.stringify(authFormData.watchlist_data)} />
            )}
          </>
        )}
        CustomFooter={
          <>
            <AuthFormSubmit label="Continue" />
            <div className="my-4">
              <Divider>
                <b className="c-fs-caption2">OR</b>
              </Divider>
            </div>
            <div className="mb-4 flex flex-col gap-2">
              <SSOGoogle redirectUrl={authFormData.redirect_url} label={ssoGoogleLabel} />
              <SSOFacebook redirectUrl={authFormData.redirect_url} label={ssoFacebookLabel} />
            </div>
            <div className="c-fs-body2 mb-8">{signupTerms}</div>
            {!isCheckout && <AuthFormFooter microcopy={footerCopies} />}
          </>
        }
      />
    </AuthFormContainer>
  );
};
